import { useEffect, useRef, useState } from "react";

// ─── Umami analytics helper ──────────────────────────────────────────────────
function useTrackCalculator(name: string, active: boolean) {
  const fired = useRef(false);
  useEffect(() => {
    if (active && !fired.current) {
      fired.current = true;
      const umami = (window as any).umami;
      if (umami?.track) umami.track(`calculator-${name}`);
    }
  }, [active, name]);
}
import { Link } from "wouter";
import { LegalFooter } from "@/components/layout/legal-footer";
import {
  Activity, ArrowLeft, Calculator, Pill, ChevronDown, ChevronUp,
  Info, Printer, User, Search, X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScoreBar, NumberInput, VASSlider } from "@/components/clinical-tools/shared";
import { JointSelector } from "@/components/clinical-tools/joint-selector";
import { ASDASCRPCalc, ASDASESRCalc } from "@/components/clinical-tools/asdas";
import { DAPSACalc } from "@/components/clinical-tools/dapsa";
import { PsARCCalc } from "@/components/clinical-tools/psarc";
import { FullHAQCalc } from "@/components/clinical-tools/full-haq";

// ─── DAS28 calculators (with joint map option) ────────────────────────────────

function DAS28CRPCalc() {
  const [useMap, setUseMap] = useState(false);
  const [tjc, setTjc] = useState(0);
  const [sjc, setSjc] = useState(0);
  const [crp, setCrp] = useState(0);
  const [gh, setGh] = useState(50);
  const das28 = 0.56 * Math.sqrt(tjc) + 0.28 * Math.sqrt(sjc) + 0.36 * Math.log(crp + 1) + 0.014 * gh + 0.96;
  useTrackCalculator("das28-crp", tjc > 0 || sjc > 0 || crp > 0 || gh !== 50);
  const bands = [
    { label: "Remission", max: 2.4,  colour: "text-emerald-700" },
    { label: "Low",       max: 2.9,  colour: "text-teal-600" },
    { label: "Moderate",  max: 5.1,  colour: "text-amber-600" },
    { label: "High",      max: 100,  colour: "text-red-600" },
  ];
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">DAS28-CRP</CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Badge variant="outline">RA</Badge>
            <Badge variant="outline">NICE biologic threshold ≥5.1</Badge>
          </div>
        </div>
        <p className="text-xs text-slate-500 font-mono">0.56√TJC28 + 0.28√SJC28 + 0.36·ln(CRP+1) + 0.014·GH + 0.96</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setUseMap(!useMap)}
            className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${useMap ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-600 border-slate-200 hover:border-teal-300"}`}
          >
            {useMap ? "✓ Using Joint Map" : "Use Visual Joint Map"}
          </button>
          {useMap && <span className="text-xs text-slate-400">TJC/SJC auto-populated from diagram</span>}
        </div>
        {useMap ? (
          <JointSelector onChange={(t, s) => { setTjc(t); setSjc(s); }} />
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <NumberInput label="Tender Joint Count (TJC28)" value={tjc} onChange={setTjc} min={0} max={28} />
            <NumberInput label="Swollen Joint Count (SJC28)" value={sjc} onChange={setSjc} min={0} max={28} />
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <NumberInput label="CRP (mg/L)" value={crp} onChange={setCrp} min={0} step={0.1} />
          <NumberInput label="Global Health — VAS (0–100 mm)" value={gh} onChange={setGh} min={0} max={100} />
        </div>
        <ScoreBar score={das28} bands={bands} />
        <p className="text-xs text-slate-500">Remission &lt;2.4 · Low &lt;2.9 · Moderate ≤5.1 · High &gt;5.1</p>
      </CardContent>
    </Card>
  );
}

function DAS28ESRCalc() {
  const [useMap, setUseMap] = useState(false);
  const [tjc, setTjc] = useState(0);
  const [sjc, setSjc] = useState(0);
  const [esr, setEsr] = useState(10);
  const [gh, setGh] = useState(50);
  const das28 = 0.56 * Math.sqrt(tjc) + 0.28 * Math.sqrt(sjc) + 0.7 * Math.log(esr) + 0.014 * gh;
  useTrackCalculator("das28-esr", tjc > 0 || sjc > 0 || esr !== 10 || gh !== 50);
  const bands = [
    { label: "Remission", max: 2.6,  colour: "text-emerald-700" },
    { label: "Low",       max: 3.2,  colour: "text-teal-600" },
    { label: "Moderate",  max: 5.1,  colour: "text-amber-600" },
    { label: "High",      max: 100,  colour: "text-red-600" },
  ];
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">DAS28-ESR</CardTitle>
          <Badge variant="outline">RA</Badge>
        </div>
        <p className="text-xs text-slate-500 font-mono">0.56√TJC28 + 0.28√SJC28 + 0.7·ln(ESR) + 0.014·GH</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setUseMap(!useMap)}
            className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${useMap ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-600 border-slate-200 hover:border-teal-300"}`}
          >
            {useMap ? "✓ Using Joint Map" : "Use Visual Joint Map"}
          </button>
        </div>
        {useMap ? (
          <JointSelector onChange={(t, s) => { setTjc(t); setSjc(s); }} />
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <NumberInput label="Tender Joint Count (TJC28)" value={tjc} onChange={setTjc} min={0} max={28} />
            <NumberInput label="Swollen Joint Count (SJC28)" value={sjc} onChange={setSjc} min={0} max={28} />
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <NumberInput label="ESR (mm/hr) — min 1" value={esr} onChange={(v) => setEsr(Math.max(1, v))} min={1} />
          <NumberInput label="Global Health — VAS (0–100 mm)" value={gh} onChange={setGh} min={0} max={100} />
        </div>
        <ScoreBar score={das28} bands={bands} />
        <p className="text-xs text-slate-500">Remission &lt;2.6 · Low ≤3.2 · Moderate ≤5.1 · High &gt;5.1</p>
      </CardContent>
    </Card>
  );
}

function CDAICalc() {
  const [useMap, setUseMap] = useState(false);
  const [tjc, setTjc] = useState(0);
  const [sjc, setSjc] = useState(0);
  const [pga, setPga] = useState(0);
  const [ega, setEga] = useState(0);
  const cdai = tjc + sjc + pga + ega;
  useTrackCalculator("cdai", tjc > 0 || sjc > 0 || pga > 0 || ega > 0);
  const bands = [
    { label: "Remission", max: 2.8,  colour: "text-emerald-700" },
    { label: "Low",       max: 10,   colour: "text-teal-600" },
    { label: "Moderate",  max: 22,   colour: "text-amber-600" },
    { label: "High",      max: 76,   colour: "text-red-600" },
  ];
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">CDAI</CardTitle>
          <Badge variant="outline">RA — no lab required</Badge>
        </div>
        <p className="text-xs text-slate-500 font-mono">TJC28 + SJC28 + PGA(0–10) + EGA(0–10)</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <button
          onClick={() => setUseMap(!useMap)}
          className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${useMap ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-600 border-slate-200 hover:border-teal-300"}`}
        >
          {useMap ? "✓ Using Joint Map" : "Use Visual Joint Map"}
        </button>
        {useMap ? (
          <JointSelector onChange={(t, s) => { setTjc(t); setSjc(s); }} />
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <NumberInput label="Tender Joint Count (TJC28)" value={tjc} onChange={setTjc} min={0} max={28} />
            <NumberInput label="Swollen Joint Count (SJC28)" value={sjc} onChange={setSjc} min={0} max={28} />
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <NumberInput label="Patient Global Assessment (0–10 cm VAS)" value={pga} onChange={setPga} min={0} max={10} step={0.1} />
          <NumberInput label="Evaluator Global Assessment (0–10 cm VAS)" value={ega} onChange={setEga} min={0} max={10} step={0.1} />
        </div>
        <ScoreBar score={cdai} bands={bands} />
        <p className="text-xs text-slate-500">Remission ≤2.8 · Low ≤10 · Moderate ≤22 · High &gt;22</p>
      </CardContent>
    </Card>
  );
}

function SDAICalc() {
  const [useMap, setUseMap] = useState(false);
  const [tjc, setTjc] = useState(0);
  const [sjc, setSjc] = useState(0);
  const [pga, setPga] = useState(0);
  const [ega, setEga] = useState(0);
  const [crp, setCrp] = useState(0);
  const sdai = tjc + sjc + pga + ega + crp;
  useTrackCalculator("sdai", tjc > 0 || sjc > 0 || pga > 0 || ega > 0 || crp > 0);
  const bands = [
    { label: "Remission", max: 3.3,  colour: "text-emerald-700" },
    { label: "Low",       max: 11,   colour: "text-teal-600" },
    { label: "Moderate",  max: 26,   colour: "text-amber-600" },
    { label: "High",      max: 86,   colour: "text-red-600" },
  ];
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">SDAI</CardTitle>
          <Badge variant="outline">RA</Badge>
        </div>
        <p className="text-xs text-slate-500 font-mono">TJC28 + SJC28 + PGA(0–10) + EGA(0–10) + CRP(mg/dL)</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <button
          onClick={() => setUseMap(!useMap)}
          className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${useMap ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-600 border-slate-200 hover:border-teal-300"}`}
        >
          {useMap ? "✓ Using Joint Map" : "Use Visual Joint Map"}
        </button>
        {useMap ? (
          <JointSelector onChange={(t, s) => { setTjc(t); setSjc(s); }} />
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <NumberInput label="Tender Joint Count (TJC28)" value={tjc} onChange={setTjc} min={0} max={28} />
            <NumberInput label="Swollen Joint Count (SJC28)" value={sjc} onChange={setSjc} min={0} max={28} />
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <NumberInput label="Patient Global Assessment (0–10 cm VAS)" value={pga} onChange={setPga} min={0} max={10} step={0.1} />
          <NumberInput label="Evaluator Global Assessment (0–10 cm VAS)" value={ega} onChange={setEga} min={0} max={10} step={0.1} />
          <NumberInput label="CRP (mg/dL) — note: mg/dL, not mg/L" value={crp} onChange={setCrp} min={0} step={0.1} />
        </div>
        <ScoreBar score={sdai} bands={bands} />
        <p className="text-xs text-slate-500">Remission ≤3.3 · Low ≤11 · Moderate ≤26 · High &gt;26</p>
      </CardContent>
    </Card>
  );
}

function SLEDAI2KCalc() {
  const items = [
    { label: "Seizure", weight: 8 },
    { label: "Psychosis", weight: 8 },
    { label: "Organic brain syndrome", weight: 8 },
    { label: "Visual disturbance", weight: 8 },
    { label: "Cranial nerve disorder", weight: 8 },
    { label: "Lupus headache (severe, persistent)", weight: 8 },
    { label: "CVA (not due to arteriosclerosis)", weight: 8 },
    { label: "Vasculitis", weight: 8 },
    { label: "Arthritis (≥2 joints with signs of inflammation)", weight: 4 },
    { label: "Myositis", weight: 4 },
    { label: "Urinary casts", weight: 4 },
    { label: "Haematuria (>5 RBC/hpf, excl. stones/infection)", weight: 4 },
    { label: "Proteinuria (>0.5 g/24h)", weight: 4 },
    { label: "Pyuria (>5 WBC/hpf, excl. infection)", weight: 4 },
    { label: "New rash", weight: 2 },
    { label: "Alopecia (new/recurrent)", weight: 2 },
    { label: "Mucosal ulcers (new/recurrent)", weight: 2 },
    { label: "Pleuritis", weight: 2 },
    { label: "Pericarditis", weight: 2 },
    { label: "Low complement (CH50, C3 or C4 < normal)", weight: 2 },
    { label: "Increased dsDNA binding (>25% Farr assay)", weight: 2 },
    { label: "Fever >38°C (after exclusion of infection)", weight: 1 },
    { label: "Thrombocytopenia (<100,000/mm³)", weight: 1 },
    { label: "Leucopenia (<3,000/mm³, after drug exclusion)", weight: 1 },
  ];
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const score = [...checked].reduce((s, i) => s + items[i].weight, 0);
  useTrackCalculator("sledai-2k", checked.size > 0);
  const toggle = (i: number) => setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });
  const bands = [
    { label: "No activity", max: 0,   colour: "text-emerald-700" },
    { label: "Mild",        max: 6,   colour: "text-teal-600" },
    { label: "Moderate",    max: 12,  colour: "text-amber-600" },
    { label: "High",        max: 105, colour: "text-red-600" },
  ];
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">SLEDAI-2K</CardTitle>
          <Badge variant="outline">SLE</Badge>
        </div>
        <p className="text-xs text-slate-500">Tick all features present in the last 10 days</p>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="grid sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
          {items.map((item, i) => (
            <label key={i} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 rounded-lg px-2 py-1">
              <input type="checkbox" checked={checked.has(i)} onChange={() => toggle(i)} className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
              <span className="text-sm text-slate-700 flex-1">{item.label}</span>
              <span className="text-xs font-semibold text-teal-600">+{item.weight}</span>
            </label>
          ))}
        </div>
        <ScoreBar score={score} bands={bands} />
        <p className="text-xs text-slate-500">Mild ≤6 · Moderate ≤12 · High &gt;12</p>
      </CardContent>
    </Card>
  );
}

function BASDAICalc() {
  const [q1, setQ1] = useState(0);
  const [q2, setQ2] = useState(0);
  const [q3, setQ3] = useState(0);
  const [q4, setQ4] = useState(0);
  const [q5, setQ5] = useState(0);
  const [q6, setQ6] = useState(0);
  const basdai = (q1 + q2 + q3 + q4 + (q5 + q6) / 2) / 5;
  useTrackCalculator("basdai", q1 > 0 || q2 > 0 || q3 > 0 || q4 > 0 || q5 > 0 || q6 > 0);
  const bands = [
    { label: "Well controlled", max: 3.9, colour: "text-emerald-700" },
    { label: "Active disease",  max: 10,  colour: "text-red-600" },
  ];
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">BASDAI</CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Badge variant="outline">axSpA / AS</Badge>
            <Badge variant="outline">NICE threshold ≥4</Badge>
          </div>
        </div>
        <p className="text-xs text-slate-500 font-mono">(Q1+Q2+Q3+Q4+mean(Q5,Q6)) ÷ 5 · All answers 0–10 NRS</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-4">
          <VASSlider label="Q1: Overall fatigue/tiredness" value={q1} onChange={setQ1} />
          <VASSlider label="Q2: Overall neck/back/hip pain" value={q2} onChange={setQ2} />
          <VASSlider label="Q3: Pain/swelling in other joints" value={q3} onChange={setQ3} />
          <VASSlider label="Q4: Discomfort from tender areas" value={q4} onChange={setQ4} />
          <VASSlider label="Q5: Morning stiffness severity" value={q5} onChange={setQ5} />
          <VASSlider label="Q6: Morning stiffness duration" value={q6} onChange={setQ6} hint="0 = 0 min, 10 = ≥2 hours" />
        </div>
        <ScoreBar score={basdai} bands={bands} />
        <p className="text-xs text-slate-500">BASDAI ≥4 = active disease requiring biologic consideration</p>
      </CardContent>
    </Card>
  );
}

const mHAQ_DOMAINS = [
  { label: "Dressing & Grooming", question: "Dress yourself, including tying shoelaces and doing buttons?" },
  { label: "Rising",              question: "Stand up from a straight chair?" },
  { label: "Eating",              question: "Cut your meat?" },
  { label: "Walking",             question: "Walk outdoors on flat ground?" },
  { label: "Hygiene",             question: "Wash and dry your entire body?" },
  { label: "Reach",               question: "Reach and get down a 5 lb (2 kg) object from just above your head?" },
  { label: "Grip",                question: "Open car doors?" },
  { label: "Activities",          question: "Run errands and shop?" },
];

const mHAQ_OPTIONS = [
  { label: "Without any difficulty", value: 0 },
  { label: "With some difficulty",   value: 1 },
  { label: "With much difficulty",   value: 2 },
  { label: "Unable to do",           value: 3 },
];

function MHAQCalc() {
  const [scores, setScores] = useState<number[]>(Array(8).fill(0));
  const rawScore = scores.reduce((s, v) => s + v, 0) / 8;
  useTrackCalculator("mhaq", scores.some(v => v > 0));
  const bands = [
    { label: "No disability", max: 0.2, colour: "text-emerald-700" },
    { label: "Mild",          max: 1.0, colour: "text-teal-600" },
    { label: "Moderate",      max: 2.0, colour: "text-amber-600" },
    { label: "Severe",        max: 3.0, colour: "text-red-600" },
  ];
  const setScore = (idx: number, val: number) => setScores((prev) => { const n = [...prev]; n[idx] = val; return n; });
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">mHAQ</CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Badge variant="outline">RA / PsA</Badge>
            <Badge variant="outline">Patient-reported</Badge>
          </div>
        </div>
        <p className="text-xs text-slate-500">Modified HAQ — 8 items. Mean score 0–3. See Full HAQ tab for the complete 20-item validated instrument.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {mHAQ_DOMAINS.map((domain, idx) => (
          <div key={idx} className="border border-slate-100 rounded-xl p-4 bg-slate-50">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">{domain.label}</p>
            <p className="text-sm text-slate-700 mb-3">Are you able to: <em>{domain.question}</em></p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {mHAQ_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setScore(idx, opt.value)}
                  className={`text-xs px-2 py-2 rounded-lg border transition-colors text-center ${scores[idx] === opt.value ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-600 border-slate-200 hover:border-teal-300"}`}
                >
                  <span className="font-bold block">{opt.value}</span>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ))}
        <ScoreBar score={rawScore} bands={bands} />
        <p className="text-xs text-slate-500">Score: {rawScore.toFixed(3)} · No disability &lt;0.2 · Mild ≤1.0 · Moderate ≤2.0 · Severe &gt;2.0</p>
      </CardContent>
    </Card>
  );
}

// ─── Drug dosage ──────────────────────────────────────────────────────────────

interface DrugEntry {
  name: string; class: string; indication: string[];
  route: string; startDose: string; maintenanceDose: string;
  maxDose: string; monitoring: string; cautions: string; ukGuideline: string;
}

const DRUG_DATA: DrugEntry[] = [
  { name: "Methotrexate (MTX)", class: "csDMARD", indication: ["RA", "PsA", "Vasculitis", "Myositis"], route: "Oral / SC injection", startDose: "7.5–10 mg once weekly", maintenanceDose: "15–20 mg once weekly", maxDose: "25–30 mg once weekly", monitoring: "FBC, U&E, LFTs at baseline then every 2–3 months. CXR at baseline.", cautions: "Avoid in pregnancy. Folate 5 mg weekly (not on same day). Renal impairment, hepatotoxicity risk.", ukGuideline: "BSR/BHPR MTX guidelines 2020" },
  { name: "Hydroxychloroquine (HCQ)", class: "csDMARD", indication: ["SLE", "UCTD", "RA (mild)"], route: "Oral", startDose: "200 mg once daily", maintenanceDose: "200–400 mg daily", maxDose: "≤5 mg/kg/day (ideal body weight)", monitoring: "Baseline ophthalmology + annual from year 5. FBC annually.", cautions: "Retinopathy risk with high doses. Dose cap in renal impairment.", ukGuideline: "RCOphth HCQ Screening guidelines 2020" },
  { name: "Sulfasalazine (SSZ)", class: "csDMARD", indication: ["RA", "PsA", "axSpA"], route: "Oral (enteric-coated)", startDose: "500 mg twice daily, increasing weekly", maintenanceDose: "1 g twice daily", maxDose: "1.5 g twice daily (3 g/day)", monitoring: "FBC, LFTs at 4 weeks, 8 weeks, 3 months then 6-monthly.", cautions: "Sulfonamide allergy contraindicated. G6PD deficiency. Men: reversible oligospermia.", ukGuideline: "BSR RA guidelines 2018" },
  { name: "Leflunomide", class: "csDMARD", indication: ["RA", "PsA"], route: "Oral", startDose: "10–20 mg daily", maintenanceDose: "20 mg daily", maxDose: "20 mg daily", monitoring: "FBC, U&E, LFTs at 4 weeks, 8 weeks, 3 months then 6-monthly. BP.", cautions: "Teratogenic — contraindicated in pregnancy. Washout with cholestyramine required. Long half-life.", ukGuideline: "BSR RA guidelines 2018" },
  { name: "Azathioprine", class: "csDMARD / Immunosuppressant", indication: ["SLE", "Vasculitis", "Myositis", "RA"], route: "Oral", startDose: "1 mg/kg/day", maintenanceDose: "2–2.5 mg/kg/day", maxDose: "3 mg/kg/day", monitoring: "TPMT genotype before starting. FBC, LFTs monthly × 3 then 3-monthly.", cautions: "Avoid with allopurinol (severe myelotoxicity). TPMT deficiency → fatal pancytopenia.", ukGuideline: "BSR SLE guidelines 2018" },
  { name: "Mycophenolate mofetil (MMF)", class: "Immunosuppressant", indication: ["Lupus nephritis", "SLE", "Vasculitis", "ILD"], route: "Oral", startDose: "500 mg twice daily", maintenanceDose: "1–1.5 g twice daily", maxDose: "1.5 g twice daily", monitoring: "FBC, U&E, LFTs monthly × 3, then 3-monthly.", cautions: "Contraindicated in pregnancy. GI side effects. Increased infection risk.", ukGuideline: "BSR SLE guidelines 2018; EULAR Lupus Nephritis 2019" },
  { name: "Prednisolone", class: "Corticosteroid", indication: ["RA (bridging)", "SLE flare", "PMR", "Vasculitis", "Myositis"], route: "Oral / IV methylprednisolone for severe flares", startDose: "PMR: 15–20 mg/day · RA: 7.5–15 mg/day · GCA: 40–60 mg/day", maintenanceDose: "Taper to lowest effective dose", maxDose: "60 mg/day oral. IV methylprednisolone 500–1000 mg for severe disease.", monitoring: "BP, weight, glucose, bone protection (FRAX + DEXA). PPI if NSAID co-prescribed.", cautions: "Bone protection mandatory if ≥7.5 mg/day for >3 months. Adrenal suppression on withdrawal.", ukGuideline: "BSR glucocorticoid guidelines 2017; NICE PMR NG65" },
];

function DrugCard({ drug }: { drug: DrugEntry }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white">
      <button onClick={() => setOpen((v) => !v)} aria-expanded={open} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-semibold text-slate-900 text-sm">{drug.name}</span>
          <Badge variant="outline" className="text-xs">{drug.class}</Badge>
          {drug.indication.map((ind) => <Badge key={ind} className="text-xs bg-teal-50 text-teal-700 border-teal-200">{ind}</Badge>)}
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
      </button>
      {open && (
        <div className="border-t border-slate-100 px-5 py-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            {[["Route", drug.route], ["Starting dose", drug.startDose], ["Maintenance dose", drug.maintenanceDose], ["Maximum dose", drug.maxDose]].map(([k, v]) => (
              <div key={k}><p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{k}</p><p className="text-sm text-slate-800">{v}</p></div>
            ))}
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3"><p className="text-xs font-semibold text-blue-800 mb-1">Monitoring</p><p className="text-xs text-blue-900">{drug.monitoring}</p></div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3"><p className="text-xs font-semibold text-amber-800 mb-1">Cautions</p><p className="text-xs text-amber-900">{drug.cautions}</p></div>
          <p className="text-xs text-slate-400">Source: {drug.ukGuideline}</p>
        </div>
      )}
    </div>
  );
}

function HCQWeightCalc() {
  const [weight, setWeight] = useState(70);
  const maxDose = Math.min(weight * 5, 400);
  useTrackCalculator("hcq-weight", weight !== 70);
  const recommended = Math.max(0, Math.floor(maxDose / 200) * 200);
  const exceedsStandardTab = maxDose < 200;
  return (
    <div className="bg-teal-50 border border-teal-200 rounded-2xl p-5 space-y-3">
      <div className="flex items-center gap-2"><Calculator className="h-4 w-4 text-teal-700" /><p className="text-sm font-semibold text-teal-800">HCQ Max Daily Dose (Retinal Safety)</p></div>
      <div className="grid sm:grid-cols-3 gap-4 items-end">
        <div>
          <label htmlFor="hcq-ibw" className="block text-xs font-medium text-slate-700 mb-1">Ideal body weight (kg)</label>
          <input id="hcq-ibw" type="number" min={30} max={200} value={weight} onChange={(e) => setWeight(parseFloat(e.target.value) || 70)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white" />
        </div>
        <div className="bg-white rounded-xl p-3 border border-teal-200 text-center"><p className="text-xs text-slate-500 mb-0.5">Max safe daily dose</p><p className="text-xl font-bold text-teal-700">{maxDose.toFixed(0)} mg</p></div>
        <div className="bg-white rounded-xl p-3 border border-slate-200 text-center"><p className="text-xs text-slate-500 mb-0.5">Safe practical dose</p><p className="text-xl font-bold text-slate-800">{exceedsStandardTab ? "—" : `${recommended} mg`}</p><p className="text-xs text-slate-400">(200 mg tablet)</p></div>
      </div>
      {exceedsStandardTab && <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 text-xs text-amber-800">Max safe dose is below 200 mg. Discuss alternate-day dosing with a consultant.</div>}
      <p className="text-xs text-teal-700">RCOphth 2020 — max 5 mg/kg IBW/day. Practical dose is always floored to avoid exceeding the safe threshold.</p>
    </div>
  );
}

function MTXDoseCalc() {
  const [weight, setWeight] = useState(70);
  const [route, setRoute] = useState<"oral" | "sc">("sc");
  const low = Math.round(weight * 0.2), high = Math.round(weight * 0.3);
  useTrackCalculator("mtx-dose", weight !== 70 || route !== "sc");
  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-5 space-y-3">
      <div className="flex items-center gap-2"><Calculator className="h-4 w-4 text-indigo-700" /><p className="text-sm font-semibold text-indigo-800">Methotrexate Weight-based Weekly Dose Guide</p></div>
      <div className="grid sm:grid-cols-3 gap-4 items-end">
        <div><label htmlFor="mtx-weight" className="block text-xs font-medium text-slate-700 mb-1">Body weight (kg)</label><input id="mtx-weight" type="number" min={30} max={200} value={weight} onChange={(e) => setWeight(parseFloat(e.target.value) || 70)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white" /></div>
        <div><p className="block text-xs font-medium text-slate-700 mb-1">Route</p><div className="flex gap-2">{(["oral", "sc"] as const).map((r) => <button key={r} onClick={() => setRoute(r)} className={`flex-1 text-xs px-3 py-2 rounded-lg border transition-colors ${route === r ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200"}`}>{r === "oral" ? "Oral" : "SC Inj."}</button>)}</div></div>
        <div className="bg-white rounded-xl p-3 border border-indigo-200 text-center"><p className="text-xs text-slate-500 mb-0.5">Dose range (weekly)</p><p className="text-xl font-bold text-indigo-700">{low}–{high} mg</p></div>
      </div>
      <div className="bg-white rounded-xl p-3 border border-slate-200 text-xs text-slate-600 space-y-1">
        <p><strong>Start:</strong> 7.5–10 mg/wk, titrate every 4–6 weeks. Cap 25–30 mg/wk.</p>
        <p><strong>SC route:</strong> Better bioavailability at doses ≥15 mg/week (CONCERTO trial).</p>
        <p><strong>Folate:</strong> 5 mg once weekly (not on MTX day).</p>
      </div>
    </div>
  );
}

function DosageSection() {
  const [search, setSearch] = useState("");
  const filtered = DRUG_DATA.filter((d) => search === "" || d.name.toLowerCase().includes(search.toLowerCase()) || d.indication.some((i) => i.toLowerCase().includes(search.toLowerCase())) || d.class.toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="space-y-5">
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search drug, indication or class…" className="w-full pl-9 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
        </div>
        {search && <button onClick={() => setSearch("")} className="px-4 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"><X className="h-4 w-4" /></button>}
      </div>
      <div className="grid gap-3">
        {filtered.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">No drugs found for "{search}"</p>}
        {filtered.map((drug) => <DrugCard key={drug.name} drug={drug} />)}
      </div>
      <div className="mt-4 space-y-4">
        <h3 className="text-base font-semibold text-slate-900">Interactive Dose Calculators</h3>
        <HCQWeightCalc />
        <MTXDoseCalc />
      </div>
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-xs text-red-800">
        <strong>Prescribing notice:</strong> Reference ranges based on BSR, NICE, and BNF. Always prescribe according to individual patient factors, renal/hepatic function, and current local formulary guidance. This tool does not replace clinical judgement.
      </div>
    </div>
  );
}

// ─── FRAX companion ───────────────────────────────────────────────────────────

const NOGG_THRESHOLDS: { age: number; mof: number; hip: number }[] = [
  { age: 50, mof: 7.5,  hip: 2.5 },
  { age: 55, mof: 9.0,  hip: 3.0 },
  { age: 60, mof: 12.0, hip: 4.5 },
  { age: 65, mof: 17.0, hip: 6.5 },
  { age: 70, mof: 22.0, hip: 10.0 },
  { age: 75, mof: 25.0, hip: 13.0 },
  { age: 80, mof: 27.0, hip: 16.0 },
];

function FraxCheckRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 rounded-lg px-2 py-1.5 transition-colors">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
      <span className="text-sm text-slate-700">{label}</span>
    </label>
  );
}

function getNOGGThreshold(age: number) {
  const sorted = [...NOGG_THRESHOLDS].sort((a, b) => a.age - b.age);
  for (let i = sorted.length - 1; i >= 0; i--) {
    if (age >= sorted[i].age) return sorted[i];
  }
  return sorted[0];
}

function FRAXCalc() {
  const [age, setAge] = useState(65);
  const [sex, setSex] = useState<"female" | "male">("female");
  const [weight, setWeight] = useState(65);
  const [height, setHeight] = useState(165);
  const [prevFx, setPrevFx] = useState(false);
  const [parentHip, setParentHip] = useState(false);
  const [smoking, setSmoking] = useState(false);
  const [glucocorticoids, setGlucocorticoids] = useState(false);
  const [gcDose, setGcDose] = useState(7.5);
  const [ra, setRa] = useState(false);
  const [secondaryOp, setSecondaryOp] = useState(false);
  const [alcohol, setAlcohol] = useState(false);
  const [hasBmd, setHasBmd] = useState(false);
  const [tScore, setTScore] = useState(-1.0);

  const bmi = weight / ((height / 100) ** 2);
  const fraxActive = age !== 65 || weight !== 65 || height !== 165 || sex !== "female" || prevFx || parentHip || smoking || glucocorticoids || ra || secondaryOp || alcohol || hasBmd;
  useTrackCalculator("frax", fraxActive);
  const threshold = getNOGGThreshold(age);

  const riskFactors = [
    prevFx && "Previous fragility fracture",
    parentHip && "Parental hip fracture",
    smoking && "Current smoker",
    glucocorticoids && `Glucocorticoids ≥5 mg/day (current dose: ${gcDose} mg)`,
    ra && "Rheumatoid arthritis",
    secondaryOp && "Secondary osteoporosis",
    alcohol && "Alcohol ≥3 units/day",
  ].filter(Boolean) as string[];

  const highDoseGC = glucocorticoids && gcDose >= 7.5;

  // Build FRAX URL (UK = country 1)
  const fraxParams = new URLSearchParams({
    country: "1",
    age: age.toString(),
    weight: weight.toString(),
    height: height.toString(),
    bmi: bmi.toFixed(1),
    ...(sex === "female" ? { female: "1" } : { male: "1" }),
    ...(prevFx    ? { priorFx:  "1" } : {}),
    ...(parentHip ? { parentFx: "1" } : {}),
    ...(smoking   ? { smoke:    "1" } : {}),
    ...(glucocorticoids ? { cortico: "1" } : {}),
    ...(ra        ? { arth:     "1" } : {}),
    ...(secondaryOp ? { secondary: "1" } : {}),
    ...(alcohol   ? { alcohol:  "1" } : {}),
  });

  const fraxUrl = `https://www.sheffield.ac.uk/FRAX/tool.aspx?${fraxParams.toString()}`;

  const CheckRow = FraxCheckRow;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">FRAX — Fracture Risk Assessment</CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Badge variant="outline">WHO Tool</Badge>
            <Badge variant="outline">Osteoporosis</Badge>
            <Badge variant="outline">GC-induced OP</Badge>
          </div>
        </div>
        <p className="text-xs text-slate-500">10-year probability of major osteoporotic and hip fracture (UK model). Enter all variables below, then open the official WHO FRAX calculator. NOGG 2022 intervention thresholds shown for your patient's age.</p>
      </CardHeader>
      <CardContent className="space-y-5">

        {/* Demographics */}
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="frax-age" className="block text-xs font-medium text-slate-700 mb-1">Age (years)</label>
            <input id="frax-age" type="number" min={40} max={90} value={age} onChange={(e) => setAge(parseInt(e.target.value) || 65)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <div>
            <p className="block text-xs font-medium text-slate-700 mb-1">Sex</p>
            <div className="flex gap-2">
              {(["female", "male"] as const).map((s) => (
                <button key={s} onClick={() => setSex(s)}
                  className={`flex-1 text-xs px-3 py-2 rounded-lg border transition-colors capitalize ${sex === s ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-600 border-slate-200 hover:border-teal-300"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <NumberInput label="Weight (kg)" value={weight} onChange={setWeight} min={20} max={200} step={0.5} />
          <NumberInput label="Height (cm)" value={height} onChange={setHeight} min={100} max={220} />
        </div>

        {/* BMI */}
        <div className={`rounded-xl p-3 border text-center ${bmi < 18.5 ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}>
          <p className="text-xs text-slate-500 mb-0.5">BMI (calculated)</p>
          <p className={`text-xl font-bold ${bmi < 18.5 ? "text-amber-700" : "text-slate-800"}`}>{bmi.toFixed(1)} kg/m²</p>
          {bmi < 18.5 && <p className="text-xs text-amber-600 mt-0.5">Low BMI is an independent risk factor — FRAX uses BMI internally</p>}
        </div>

        {/* Clinical risk factors */}
        <div>
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Clinical Risk Factors</p>
          <div className="grid sm:grid-cols-2 gap-0.5">
            <CheckRow label="Previous fragility fracture" checked={prevFx} onChange={setPrevFx} />
            <CheckRow label="Parent fractured hip" checked={parentHip} onChange={setParentHip} />
            <CheckRow label="Current smoker" checked={smoking} onChange={setSmoking} />
            <CheckRow label="Rheumatoid arthritis" checked={ra} onChange={setRa} />
            <CheckRow label="Secondary osteoporosis" checked={secondaryOp} onChange={setSecondaryOp} />
            <CheckRow label="Alcohol ≥3 units/day" checked={alcohol} onChange={setAlcohol} />
            <CheckRow label="Glucocorticoids (≥5 mg/day, ≥3 months)" checked={glucocorticoids} onChange={setGlucocorticoids} />
          </div>
          {glucocorticoids && (
            <div className="mt-2 ml-6">
              <NumberInput label="Current prednisolone dose (mg/day)" value={gcDose} onChange={setGcDose} min={0} max={100} step={0.5} />
              {highDoseGC && (
                <div className="mt-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 text-xs text-orange-800">
                  <strong>NOGG 2022:</strong> Prednisolone ≥7.5 mg/day → apply a dose correction factor. The WHO FRAX tool uses a standard GC exposure flag; for high-dose GC (≥7.5 mg), NOGG recommends multiplying the FRAX major fracture probability by 1.15 and hip fracture by 1.2. Consider bone protection therapy regardless of FRAX score if continuous GC ≥7.5 mg/day for anticipated ≥3 months.
                </div>
              )}
            </div>
          )}
        </div>

        {/* BMD (optional) */}
        <div>
          <CheckRow label="Include femoral neck BMD (T-score)" checked={hasBmd} onChange={setHasBmd} />
          {hasBmd && (
            <div className="mt-2 ml-6">
              <NumberInput label="Femoral neck T-score" value={tScore} onChange={setTScore} min={-4} max={2} step={0.1} />
              <p className="text-xs text-slate-400 mt-1">Normal ≥−1.0 · Osteopenia −1.0 to −2.5 · Osteoporosis ≤−2.5</p>
            </div>
          )}
        </div>

        {/* Risk factor summary */}
        {riskFactors.length > 0 && (
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-3">
            <p className="text-xs font-semibold text-rose-700 mb-2">{riskFactors.length} risk factor{riskFactors.length > 1 ? "s" : ""} identified</p>
            <ul className="space-y-1">
              {riskFactors.map((rf) => (
                <li key={rf} className="flex items-start gap-1.5 text-xs text-rose-800">
                  <span className="mt-0.5 shrink-0">•</span>{rf}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* NOGG thresholds */}
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 space-y-2">
          <p className="text-xs font-semibold text-teal-800">NOGG 2022 — UK Intervention Thresholds (age {age})</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-lg p-3 border border-teal-100 text-center">
              <p className="text-xs text-slate-500 mb-0.5">Major osteoporotic fracture</p>
              <p className="text-lg font-bold text-teal-700">≥{threshold.mof}%</p>
              <p className="text-xs text-slate-400">10-year risk → treat</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-teal-100 text-center">
              <p className="text-xs text-slate-500 mb-0.5">Hip fracture</p>
              <p className="text-lg font-bold text-teal-700">≥{threshold.hip}%</p>
              <p className="text-xs text-slate-400">10-year risk → treat</p>
            </div>
          </div>
          <p className="text-xs text-teal-700">Thresholds represent the intervention boundary for that 5-year age band. Rounded from NOGG 2022 guidance — always confirm with current NOGG tables.</p>
        </div>

        {/* CTA to official FRAX tool */}
        <div className="bg-slate-900 rounded-xl p-4 text-white space-y-3">
          <div>
            <p className="text-sm font-semibold">Calculate final FRAX score — Official WHO Tool</p>
            <p className="text-xs text-slate-300 mt-1">The full FRAX calculation uses a validated country-specific statistical model (UK). Click below to open the official Sheffield FRAX calculator — your values above are summarised as a pre-check reference.</p>
          </div>
          <a
            href={fraxUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
          >
            Open FRAX UK Calculator →
          </a>
          <p className="text-xs text-slate-400">Opens shef.ac.uk/FRAX · No patient data is transmitted from RheumHub</p>
        </div>

        {/* Guidance notes */}
        <div className="text-xs text-slate-500 space-y-1 border-t border-slate-100 pt-3">
          <p><strong>Bone protection triggers (NOGG/BSR):</strong> Start bisphosphonate if: FRAX ≥ intervention threshold · or GC ≥7.5 mg/day for ≥3 months (regardless of FRAX) · or age &gt;70 + prevalent vertebral fracture.</p>
          <p><strong>DEXA:</strong> If FRAX near threshold without BMD, arrange DXA to refine risk before treatment decision.</p>
          <p><strong>Monitoring:</strong> DXA every 2 years for patients on long-term GC. Review FRAX annually or if risk factors change.</p>
        </div>

      </CardContent>
    </Card>
  );
}

// ─── Scoring section tabs ─────────────────────────────────────────────────────

const SCORE_GROUPS = [
  {
    label: "RA",
    tools: [
      { id: "das28crp", label: "DAS28-CRP" },
      { id: "das28esr", label: "DAS28-ESR" },
      { id: "cdai",     label: "CDAI" },
      { id: "sdai",     label: "SDAI" },
      { id: "mhaq",     label: "mHAQ" },
      { id: "fullhaq",  label: "Full HAQ" },
    ],
  },
  {
    label: "axSpA / AS",
    tools: [
      { id: "basdai",    label: "BASDAI" },
      { id: "asdas-crp", label: "ASDAS-CRP" },
      { id: "asdas-esr", label: "ASDAS-ESR" },
    ],
  },
  {
    label: "PsA",
    tools: [
      { id: "dapsa", label: "DAPSA" },
      { id: "psarc", label: "PsARC" },
    ],
  },
  {
    label: "SLE",
    tools: [
      { id: "sledai", label: "SLEDAI-2K" },
    ],
  },
  {
    label: "Bone",
    tools: [
      { id: "frax", label: "FRAX" },
    ],
  },
];

function ScoringSection() {
  const [active, setActive] = useState("das28crp");
  return (
    <div>
      <div className="space-y-2 mb-6">
        {SCORE_GROUPS.map((group) => (
          <div key={group.label} className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider w-16 shrink-0">{group.label}</span>
            <div className="flex flex-wrap gap-1.5">
              {group.tools.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActive(t.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${active === t.id ? "bg-teal-700 text-white border-teal-700" : "bg-white text-slate-700 border-slate-200 hover:border-teal-300 hover:text-teal-700"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {active === "das28crp"  && <DAS28CRPCalc />}
      {active === "das28esr"  && <DAS28ESRCalc />}
      {active === "cdai"      && <CDAICalc />}
      {active === "sdai"      && <SDAICalc />}
      {active === "mhaq"      && <MHAQCalc />}
      {active === "fullhaq"   && <FullHAQCalc />}
      {active === "basdai"    && <BASDAICalc />}
      {active === "asdas-crp" && <ASDASCRPCalc />}
      {active === "asdas-esr" && <ASDASESRCalc />}
      {active === "dapsa"     && <DAPSACalc />}
      {active === "psarc"     && <PsARCCalc />}
      {active === "sledai"    && <SLEDAI2KCalc />}
      {active === "frax"      && <FRAXCalc />}

      <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800">
        <strong>Clinical note:</strong> Scores support treat-to-target monitoring but must be interpreted alongside clinical examination, imaging, and patient-reported outcomes. NICE biologic thresholds (DAS28 &gt;5.1, BASDAI ≥4, ASDAS ≥2.1) are eligibility criteria — prescribing decisions require consultant assessment.
      </div>
    </div>
  );
}

// ─── Tool quick-access cards ──────────────────────────────────────────────────

const TOOL_CARDS = [
  { label: "DAS28",      sub: "RA activity",   color: "bg-teal-50 border-teal-200 text-teal-700",   tab: "scoring", tool: "das28crp"  },
  { label: "ASDAS",      sub: "axSpA / AS",    color: "bg-indigo-50 border-indigo-200 text-indigo-700", tab: "scoring", tool: "asdas-crp" },
  { label: "BASDAI",     sub: "axSpA / AS",    color: "bg-violet-50 border-violet-200 text-violet-700", tab: "scoring", tool: "basdai"   },
  { label: "DAPSA",      sub: "PsA activity",  color: "bg-rose-50 border-rose-200 text-rose-700",  tab: "scoring", tool: "dapsa"    },
  { label: "SLEDAI-2K",  sub: "SLE activity",  color: "bg-blue-50 border-blue-200 text-blue-700",  tab: "scoring", tool: "sledai"   },
  { label: "Full HAQ",   sub: "Function",      color: "bg-amber-50 border-amber-200 text-amber-700", tab: "scoring", tool: "fullhaq"  },
  { label: "PsARC",      sub: "PsA response",  color: "bg-pink-50 border-pink-200 text-pink-700",  tab: "scoring", tool: "psarc"    },
  { label: "FRAX",      sub: "Fracture risk", color: "bg-purple-50 border-purple-200 text-purple-700", tab: "scoring", tool: "frax"    },
  { label: "Drug Doses", sub: "DMARD guide",  color: "bg-slate-50 border-slate-200 text-slate-700", tab: "dosage", tool: null       },
];

type Tab = "scoring" | "dosage";

export default function ClinicalTools() {
  const [tab, setTab]           = useState<Tab>("scoring");
  const [activeTool, setActiveTool] = useState("das28crp");
  const [patientId, setPatientId]   = useState("");

  // Track clinical tools page view
  useEffect(() => { (window as any).umami?.track?.("page-clinical-tools"); }, []);

  const jumpTo = (t: Tab, tool: string | null) => {
    setTab(t);
    if (tool) setActiveTool(tool);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-show { display: block !important; }
          header, footer { position: static !important; }
          body { background: white !important; }
          .shadow, [class*="shadow-"] { box-shadow: none !important; }
        }
      `}</style>

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 no-print">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center gap-4">
          <Link href="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors text-sm">
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">RheumHub</span>
          </Link>
          <div className="flex items-center gap-2 flex-1">
            <Activity className="h-5 w-5 text-teal-700" />
            <span className="font-bold text-slate-800">Clinical Tools</span>
            <span className="hidden sm:inline text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">13 calculators</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-1.5 text-slate-600 hover:text-slate-900 transition-colors"
              title="Print / Save PDF"
            >
              <Printer className="h-4 w-4" />
              <span className="hidden sm:inline">Print</span>
            </button>
            <Link href="/sign-in" className="text-slate-600 hover:text-slate-900 transition-colors font-medium">Log in</Link>
            <Link href="/sign-up" className="bg-teal-700 hover:bg-teal-800 text-white px-4 py-1.5 rounded-full text-sm font-medium transition-colors">Sign up</Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 w-full flex-1">

        {/* Hero */}
        <div className="mb-6 no-print">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 text-teal-800 text-xs font-medium mb-4 border border-teal-100">
            <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"/><span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"/></span>
            Free · No account needed · NICE / BSR / EULAR / ASAS / GRAPPA aligned
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">UK Rheumatology Clinical Calculators</h1>
          <p className="text-slate-600 max-w-2xl">
            DAS28 · ASDAS · BASDAI · SLEDAI-2K · DAPSA · PsARC · Full HAQ · CDAI · SDAI and more.
            Visual 28-joint mapping, VAS sliders, and direct PDF/print export with patient reference.
          </p>
          <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
            <Info className="h-3.5 w-3.5 shrink-0" />
            <span>All calculations happen in your browser. No data is stored or transmitted to any server.</span>
          </div>
        </div>

        {/* Patient / session identifier */}
        <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 mb-6 flex items-center gap-3 flex-wrap">
          <User className="h-4 w-4 text-slate-400 shrink-0" />
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <label htmlFor="patient-ref-input" className="text-xs font-medium text-slate-600 shrink-0">Patient reference</label>
            <input
              id="patient-ref-input"
              type="text"
              value={patientId}
              onChange={(e) => setPatientId(e.target.value)}
              placeholder="e.g. initials, NHS number, or study ID"
              className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          {patientId && (
            <button onClick={() => setPatientId("")} className="text-xs text-slate-400 hover:text-slate-700 flex items-center gap-1">
              <X className="h-3 w-3" /> Clear
            </button>
          )}
          <button onClick={() => window.print()} className="no-print flex items-center gap-1.5 text-xs text-teal-700 hover:text-teal-900 border border-teal-200 rounded-lg px-3 py-1.5 hover:bg-teal-50 transition-colors">
            <Printer className="h-3.5 w-3.5" /> Export / Print
          </button>
          <p className="w-full text-xs text-slate-500 mt-1">
            <span className="font-medium text-slate-500">GDPR note:</span> Patient reference is stored only in this browser tab and is never transmitted. Use initials or a reference number rather than a full name where possible.
          </p>
        </div>

        {/* Print header — only visible when printing */}
        <div className="hidden print-show mb-6 border-b pb-4">
          <p className="text-xl font-bold">RheumHub — Clinical Tool Export</p>
          {patientId && <p className="text-sm text-slate-600">Patient reference: <strong>{patientId}</strong></p>}
          <p className="text-xs text-slate-400">Printed: {new Date().toLocaleDateString("en-GB", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
        </div>

        {/* Quick-access cards */}
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 mb-8 no-print">
          {TOOL_CARDS.map((c) => (
            <button
              key={c.label}
              onClick={() => jumpTo(c.tab as Tab, c.tool)}
              className={`rounded-xl border p-2.5 text-left transition-all hover:shadow-sm hover:-translate-y-0.5 ${c.color}`}
            >
              <p className="text-xs font-bold leading-tight">{c.label}</p>
              <p className="text-xs mt-0.5 leading-tight">{c.sub}</p>
            </button>
          ))}
        </div>

        {/* Main tab bar */}
        <div className="flex gap-1 border-b border-slate-200 mb-8 no-print">
          {(["scoring", "dosage"] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${tab === t ? "border-teal-600 text-teal-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}
            >
              {t === "scoring" ? <><Calculator className="h-4 w-4" />Scoring Calculators</> : <><Pill className="h-4 w-4" />Drug Dosage Reference</>}
            </button>
          ))}
        </div>

        {tab === "scoring" && <ScoringSection key={activeTool} />}
        {tab === "dosage"  && <DosageSection />}
      </main>

      <LegalFooter
        copyright={`© ${new Date().getFullYear()} RheumHub — For UK healthcare professionals. Not a substitute for clinical judgement or local formulary guidance.`}
        extraLinks={[{ label: "Home", href: "/" }]}
      />
    </div>
  );
}
